import { useAuth0 } from '@auth0/auth0-react';
import { useEffect, useRef, useState, useCallback } from 'react';

const REFRESH_INTERVAL = 5 * 60 * 1000; // 5 minutes

/**
 * Hook that proactively refreshes Auth0 tokens to prevent session expiration
 * during user activity. This prevents disruptive page reloads when a user
 * returns after being idle.
 *
 * Features:
 * - Refreshes tokens every 5 minutes when tab is visible
 * - Pauses refresh when tab is hidden (saves resources)
 * - Immediately refreshes when user returns to a hidden tab
 */
export function useTokenRefresh() {
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [lastRefresh, setLastRefresh] = useState(Date.now());
  const [isVisible, setIsVisible] = useState(!document.hidden);

  const refreshToken = useCallback(async () => {
    try {
      // Force a fresh token fetch, bypassing cache
      await getAccessTokenSilently({ cacheMode: 'off' });
      setLastRefresh(Date.now());
    } catch (error) {
      // Log but don't throw - let the 401 handler deal with truly expired sessions
      console.warn('[useTokenRefresh] Token refresh failed:', error);
    }
  }, [getAccessTokenSilently]);

  // Track tab visibility
  useEffect(() => {
    const handleVisibility = () => setIsVisible(!document.hidden);
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, []);

  // Refresh when tab becomes visible after being hidden for a while
  useEffect(() => {
    if (isVisible && isAuthenticated && Date.now() - lastRefresh > REFRESH_INTERVAL) {
      refreshToken();
    }
  }, [isVisible, isAuthenticated, lastRefresh, refreshToken]);

  // Periodic heartbeat (only when visible and authenticated)
  useEffect(() => {
    if (!isAuthenticated || !isVisible) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    intervalRef.current = setInterval(refreshToken, REFRESH_INTERVAL);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isAuthenticated, isVisible, refreshToken]);

  return { lastRefresh };
}
